import React, {useCallback, useEffect, useState} from 'react';
import Select from 'react-select';
import { DateTime } from "luxon";
import {brandles, initialStorageData, stylesForSelect} from '../../utils/constants'
import {GameStatus, IStorageGameData, TBrand, TStat} from "../../types";
import GuessingField from "./components/GuessingField";
import Share from "./components/Share";

import "./Form.component.scss";

type Props = {
  currentGame: TBrand | undefined;
  getCurrentGameData: (data: IStorageGameData) => void;
  gameNumber: number;
};

/* ----- Main Component ----- */
const Form = (props: Props) => {
  const [selectedOption, setSelectedOption] = useState<TBrand | null>();
  const [gamesData, setGamesData] = useState<IStorageGameData>(initialStorageData);
  const [disableGame, setDisableGame] = useState<boolean>(false);

  const { currentGame, getCurrentGameData, gameNumber } = props;
  const guessingFields: JSX.Element[] = [];

  const setGameData = useCallback(
    (gameData: IStorageGameData) => {
      setGamesData(gameData);
      getCurrentGameData(gameData);
    },
    [getCurrentGameData, setGamesData]
  );

  useEffect(() => {
    const storageData = localStorage.getItem("gamesData");

    if (!storageData) {
      localStorage.setItem("gamesData", JSON.stringify(initialStorageData));
      setGameData(initialStorageData);
    } else {
      setGameData(JSON.parse(storageData));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('gamesData', JSON.stringify(gamesData));

    if (currentGame && currentGame.id !== gamesData?.currentGame.gameID) {
      const editableGameData: IStorageGameData = {
        stat: {
          ...gamesData.stat
        },
        currentGame: {
          gameID: currentGame.id,
          guesses: []
        }
      };
      setGameData(editableGameData);
      setDisableGame(false);
    }
  }, [gamesData, currentGame, setGameData]);

  useEffect(() => {
    if (
      gamesData?.currentGame.guesses.length === 5 ||
      gamesData?.currentGame.guesses.find(
        (item: string) => item === props.currentGame?.name
      )
    ) {
      setDisableGame(true);
    }
  }, [gamesData, selectedOption]);

  const handleChange = (selectedOption: any) => {
    setSelectedOption(selectedOption);
  };

  const changeStatData = (stat: TStat, isWin: boolean): TStat => {
    const { guesses, lastGameStatus, Win, Played, MaxStreak, CurrentStreak, lastPlayedDate } =
      stat;      

    const changeStatGuesses = () => {
      if (isWin && gamesData) {
        const guessWinLength: number = gamesData.currentGame.guesses.length;
        let editableGuesses = [...guesses];

        editableGuesses[guessWinLength] = guesses[guessWinLength] + 1;
        return editableGuesses;
      } else return stat.guesses;
    };

    if (stat.guesses.length === 5 || isWin) {
      const isAttemptsCompleted: boolean =
        gamesData?.currentGame.guesses.length === 4;

      return {
        ...stat,
        CurrentStreak:
          isWin &&
          lastGameStatus === GameStatus.VICTORY &&
          DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd") ===
            lastPlayedDate
            ? CurrentStreak + 1
            : isWin &&
          lastGameStatus === GameStatus.VICTORY &&
          DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd") !==
            lastPlayedDate ? 1:
            !isWin && isAttemptsCompleted
            ? 0
            : CurrentStreak,
        MaxStreak:
          isWin &&
          lastGameStatus === GameStatus.VICTORY &&
          CurrentStreak >= MaxStreak &&
          DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd") ===
            lastPlayedDate
            ? MaxStreak + 1
            : MaxStreak,
        Played: isAttemptsCompleted || isWin ? Played + 1 : Played,
        Win: isWin ? Win + 1 : Win,
        guesses: changeStatGuesses(),
        lastGameStatus: isWin
          ? GameStatus.VICTORY
          : !isWin && isAttemptsCompleted
          ? GameStatus.LOST
          : GameStatus.VICTORY,
        lastPlayedDate:
          isAttemptsCompleted || isWin ? DateTime.now().toFormat("yyyy-MM-dd") : lastPlayedDate,
      };
    } else return stat;
  };

  const setValue = () => {
    if (gamesData && selectedOption) {
      const isWin: boolean = !!(
        selectedOption.name === props.currentGame?.name
      );

      const editableCurrentData: IStorageGameData = {
        ...gamesData,
        currentGame: {
          ...gamesData.currentGame,
          guesses: [
            ...gamesData.currentGame.guesses,
            selectedOption.name ? selectedOption.name : "",
          ],
        },
        stat: changeStatData(gamesData.stat, isWin),
      };

      setGameData(editableCurrentData);
      setSelectedOption(null);
    }
  };

  for (let i = 0; i < 5; i++) {
    guessingFields.push(
      <GuessingField
      inputData={gamesData.currentGame.guesses?.[i] || ""}
      currentGame={currentGame}
      key={i}
      />
    );
  }

  if (gamesData && currentGame) {    

    return !disableGame ? (
      <div className="form">
        {guessingFields.map((item: JSX.Element) => item)}

        <Select
          value={selectedOption}
          onChange={handleChange}
          options={brandles.brands}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.name}
          styles={stylesForSelect}
          placeholder="Search For A Brand"
          isClearable
          hideSelectedOptions
          maxMenuHeight={190}
          isDisabled={disableGame}
          openMenuOnFocus={false}
          openMenuOnClick={false}
        />

        <button
          className="form__submit-button"
          onClick={setValue}
          disabled={!selectedOption}
        >
          Guess
        </button>
      </div>
    ) : (
      <>
        <Share
          gameData={gamesData}
          brandName={currentGame.name}
          gameNumber={gameNumber}
        />
        {guessingFields.map((item: JSX.Element) => item)}
      </>
    );
  } else return null;
};

export default Form;
